// src/features/assistant-connect/components/AssistantConnectEntry.tsx

import React from 'react';
import { useTranslate } from 'react-admin';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { ButtonBase, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { AutoAwesomeRounded as AssistantIcon } from '@mui/icons-material';
import { getCurrentRole } from '@/utils/auth';
import { ASSISTANT_CONNECT_PATH } from '../constants';
import { canConnectAssistant } from '../utils/access';

/** Visible sizes inside the 52px bar: the labelled pill, and the round icon button on a phone. */
const PILL_HEIGHT = 32;
const ROUND_SIZE = 36;
/** Minimum touch target the hit area is extended to. */
const TOUCH_TARGET = 44;

/**
 * The app-bar entry to «Conecte su asistente de IA». It is a pill in the bar's own state-tint
 * recipe (the brand teal at low alpha as the fill, its dark ink for the label and the icon, fully
 * rounded), so it reads as a destination of its own next to «Preguntar». From `md` up it carries
 * its label; below, the bar has no room for words and it becomes a round icon button with the same
 * accessible name and a tooltip. Shown only to the roles the assistant server accepts; under
 * impersonation the effective role decides.
 */
export const AssistantConnectEntry: React.FC = () => {
  const translate = useTranslate();
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down('md'));
  const { pathname } = useLocation();

  if (!canConnectAssistant(getCurrentRole())) return null;

  const current = pathname === ASSISTANT_CONNECT_PATH ? 'page' : undefined;
  const label = translate('assistantConnect.entry');
  const tint = theme.custom.status.opportunity;

  return (
    // The tooltip describes the entry; its accessible name stays the visible label.
    <Tooltip title={translate('assistantConnect.entryTooltip')} describeChild>
      <ButtonBase
        component={RouterLink}
        to={ASSISTANT_CONNECT_PATH}
        aria-current={current}
        aria-label={isNarrow ? label : undefined}
        sx={{
          flexShrink: 0,
          gap: 0.75,
          mr: 1,
          borderRadius: theme.custom.radius.full,
          // Resting, hover and current deepen the same tint; current also takes the heavier weight,
          // so it never depends on the fill alone.
          backgroundColor: current ? alpha(theme.palette.primary.dark, 0.18) : tint.bg,
          color: tint.text,
          fontSize: '0.8125rem',
          fontWeight: current ? 600 : 500,
          lineHeight: 1,
          whiteSpace: 'nowrap',
          transition: theme.transitions.create(['background-color', 'box-shadow'], {
            duration: theme.transitions.duration.shorter,
          }),
          '& .MuiSvgIcon-root': { fontSize: 18, color: theme.palette.primary.dark },
          '&:hover': { backgroundColor: alpha(theme.palette.primary.dark, current ? 0.22 : 0.16) },
          // The global focus rule squares every focused element to the small radius; the pill keeps its shape.
          '&:focus-visible, &.Mui-focusVisible': {
            boxShadow: theme.custom.focusRing,
            borderRadius: theme.custom.radius.full,
          },
          ...(isNarrow ? { width: ROUND_SIZE, height: ROUND_SIZE } : { height: PILL_HEIGHT, px: 1.5 }),
          // The visible shape stays small inside the 52px bar; the hit area reaches the touch minimum.
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: isNarrow
              ? `-${(TOUCH_TARGET - ROUND_SIZE) / 2}px`
              : `-${(TOUCH_TARGET - PILL_HEIGHT) / 2}px 0`,
          },
        }}
      >
        <AssistantIcon aria-hidden />
        {!isNarrow && <span>{label}</span>}
      </ButtonBase>
    </Tooltip>
  );
};

export default AssistantConnectEntry;
